import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ToggleArrowIcon } from "@common/assets/toggleArrowIcon";
import { SelectList } from "@common/selectList";
import { useLanguage } from "@common/language";
import { TextInput } from "@common/textInput";
import { Props } from "./types";
import styles from "./styles.scss";
import { Checkbox } from "@common/checkbox";

function sanitize(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function match(name: string, search: string) {
  return new RegExp(sanitize(search), "i").test(name);
}

const SEPARATOR = ":::";

function ListForm({ value, attr, options, readOnly, disabled, submit }: Props) {
  const { currentLanguage } = useLanguage();
  const [query, setQuery] = React.useState("");
  const { t } = useTranslation();
  const values = value.split(SEPARATOR).filter((val) => val.length > 0);
  const isMultiple = attr.multiple === "true";
  const translate = (value: string) => {
    const option = options.find(({ name }) => name === value);

    return option ? option.labels[currentLanguage] || value : value;
  };
  const select = (val: string) =>
    submit(isMultiple ? [...values, val].join(SEPARATOR) : val);
  const unSelect = (val: string) =>
    submit(values.filter((v) => v !== val).join(SEPARATOR));

  return readOnly ? (
    <div className={classNames(styles.listForm, styles.readOnly)}>
      <p className={styles.input}>
        {!!value ? values.map(translate).join(", ") : ""}
      </p>
    </div>
  ) : (
    <SelectList
      rootClassName={classNames(styles.listForm, {
        [styles.disabled]: disabled,
      })}
      listClassName={styles.listRoot}
      isDisabled={disabled}
      multiple={isMultiple}
    >
      {({ isOpen, select: s }) => ({
        input: (
          <div className={classNames(styles.input, { [styles.open]: isOpen })}>
            <p className={styles.name}>
              {!!value ? values.map(translate).join(", ") : "-"}
            </p>
            <ToggleArrowIcon className={styles.icon} />
          </div>
        ),
        list: (
          <>
            <div className={styles.header}>
              <TextInput
                className={styles.textInput}
                placeholder={t("common.search")}
                value={query}
                input={setQuery}
              />
            </div>
            <div className={styles.list}>
              {options
                .filter(({ name }) => match(translate(name), query))
                .map((option) => {
                  const isSelected = !!values.find(
                    (val) => val === option.name,
                  );

                  return (
                    <div
                      key={option.uuid}
                      className={classNames(styles.item, {
                        [styles.selected]: isSelected,
                        [styles.multiple]: isMultiple,
                      })}
                      onClick={() =>
                        !disabled &&
                        s(
                          !isSelected
                            ? select(option.name)
                            : unSelect(option.name),
                        )
                      }
                    >
                      {isMultiple && <Checkbox isChecked={isSelected} />}
                      <p className={styles.name}>{translate(option.name)}</p>
                    </div>
                  );
                })}
            </div>
          </>
        ),
      })}
    </SelectList>
  );
}

export { ListForm };
